/**
 * Analytics Utilities for PeopleTag
 * Functions for tracking and analyzing user behavior and profile statistics
 */
import { db } from '../firebase';
import { collection, doc, setDoc, getDoc, updateDoc, increment, serverTimestamp, query, where, getDocs, orderBy, limit } from 'firebase/firestore';

/**
 * Track profile view
 * @param {string} viewedUsername - Username of viewed profile
 * @param {string} viewerWalletAddress - Wallet address of viewer (null/undefined for unlogged users)
 * @param {string} viewerSessionId - Session ID for unlogged users (optional, generated if not provided)
 */
export const trackProfileView = async (viewedUsername, viewerWalletAddress, viewerSessionId = null) => {
  try {
    if (!viewedUsername) {
      console.warn('trackProfileView: No viewedUsername provided');
      return;
    }

    // Normalize wallet address - treat empty string, null, or undefined as no wallet
    const normalizedWalletAddress = viewerWalletAddress && viewerWalletAddress.trim() ? viewerWalletAddress.trim().toLowerCase() : null;

    // For unlogged users, use a session ID to track unique viewers
    // Generate session ID if not provided (for unlogged users)
    if (!normalizedWalletAddress) {
      if (!viewerSessionId) {
        // Try to get existing session ID from sessionStorage, or generate new one
        const sessionKey = 'peopletag_anonymous_session_id';
        viewerSessionId = sessionStorage.getItem(sessionKey);
        if (!viewerSessionId) {
          viewerSessionId = `anonymous_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem(sessionKey, viewerSessionId);
        }
      }
    }

    if (!normalizedWalletAddress && !viewerSessionId) {
      console.warn('trackProfileView: No viewer identifier available');
      return;
    }


    // Check if this is a unique viewer BEFORE storing the view
    let isUniqueViewer = false;
    try {
      if (normalizedWalletAddress) {
        // Check if this wallet address has viewed before
        const existingViewsQuery = query(
          collection(db, 'profileViews'),
          where('viewedUsername', '==', viewedUsername),
          where('viewerWalletAddress', '==', normalizedWalletAddress),
          limit(1)
        );
        const existingViews = await getDocs(existingViewsQuery);
        isUniqueViewer = existingViews.empty;
      } else if (viewerSessionId) {
        // Check if this session has viewed before
        // Note: This query might require a Firestore index, but we'll handle errors gracefully
        try {
          const existingViewsQuery = query(
            collection(db, 'profileViews'),
            where('viewedUsername', '==', viewedUsername),
            where('viewerSessionId', '==', viewerSessionId),
            limit(1)
          );
          const existingViews = await getDocs(existingViewsQuery);
          isUniqueViewer = existingViews.empty;
        } catch (queryError) {
          // If query fails (e.g., missing index), fallback: fetch all views and check client-side
          try {
            const allViewsQuery = query(
              collection(db, 'profileViews'),
              where('viewedUsername', '==', viewedUsername),
              limit(100) // Limit to avoid fetching too many
            );
            const allViews = await getDocs(allViewsQuery);
            // Check if any view has this sessionId
            const hasViewed = allViews.docs.some(doc => {
              const data = doc.data();
              return data.viewerSessionId === viewerSessionId;
            });
            isUniqueViewer = !hasViewed;
          } catch (fallbackError) {
            // If fallback also fails, assume it's unique
            console.warn('Could not check for unique unlogged viewer, assuming unique:', fallbackError);
            isUniqueViewer = true;
          }
        }
      }
    } catch (error) {
      console.error('Error checking for unique viewer:', error);
      // If check fails, assume it's unique to be safe (better to overcount than undercount)
      isUniqueViewer = true;
    }

    // Try to get viewer's username if they're logged in
    let viewerUsername = null;
    if (normalizedWalletAddress) {
      try {
        // Import getUsernameFromWallet dynamically to avoid circular dependency
        const { getUsernameFromWallet } = await import('../userMappingService');
        viewerUsername = await getUsernameFromWallet(normalizedWalletAddress);
      } catch (error) {
        // Silently fail - username lookup is optional
      }
    }

    const viewData = {
      viewedUsername,
      viewerWalletAddress: normalizedWalletAddress || null,
      viewerSessionId: viewerSessionId || null,
      viewerUsername: viewerUsername || null, // Store username directly in view record
      isLoggedIn: !!normalizedWalletAddress,
      timestamp: Date.now(),
      date: new Date().toISOString().split('T')[0],
      createdAt: serverTimestamp()
    };

    // Store in Firestore - create a document in profileViews collection
    // Document ID: viewedUsername_viewerIdentifier_timestamp (to allow multiple views)
    const viewerIdentifier = normalizedWalletAddress || viewerSessionId;
    const viewDocId = `${viewedUsername}_${viewerIdentifier}_${Date.now()}`;
    const viewRef = doc(db, 'profileViews', viewDocId);
    
    await setDoc(viewRef, viewData);

    // Also update the profile's view statistics document
    const statsRef = doc(db, 'profileViewStats', viewedUsername);
    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      // Update existing stats
      const updateData = {
        totalViews: increment(1),
        lastViewAt: serverTimestamp()
      };
      
      // Update unique viewers count if this is a new unique viewer
      if (isUniqueViewer) {
        if (normalizedWalletAddress) {
          updateData.uniqueLoggedInViewers = increment(1);
        } else if (viewerSessionId) {
          updateData.uniqueUnloggedViewers = increment(1);
        }
      }
      
      await updateDoc(statsRef, updateData);
    } else {
      // Create new stats document
      const newStats = {
        viewedUsername,
        totalViews: 1,
        uniqueLoggedInViewers: (normalizedWalletAddress && isUniqueViewer) ? 1 : 0,
        uniqueUnloggedViewers: (viewerSessionId && isUniqueViewer) ? 1 : 0,
        createdAt: serverTimestamp(),
        lastViewAt: serverTimestamp()
      };
      await setDoc(statsRef, newStats);
      console.log('Profile view stats updated:', { viewedUsername, totalViews: 1, isUniqueViewer });
    }
  } catch (error) {
    console.error('Error tracking profile view:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    // Fallback to localStorage if Firestore fails
    try {
      const key = `peopletag_views_${viewedUsername}`;
      const existingViews = JSON.parse(localStorage.getItem(key) || '[]');
      existingViews.push({
        viewedUsername,
        viewerWalletAddress: viewerWalletAddress || null,
        viewerSessionId: viewerSessionId || null,
        isLoggedIn: !!viewerWalletAddress,
        timestamp: Date.now(),
        date: new Date().toISOString().split('T')[0]
      });
      if (existingViews.length > 1000) {
        existingViews.shift();
      }
      localStorage.setItem(key, JSON.stringify(existingViews));
    } catch (localError) {
      console.error('Error storing view in localStorage fallback:', localError);
    }
  }
};

/**
 * Get profile view statistics
 * @param {string} username - Username
 * @returns {Promise<Object>} View statistics
 */
export const getProfileViewStats = async (username) => {
  try {
    if (!username) {
      return {
        totalViews: 0,
        uniqueViewers: 0,
        uniqueLoggedInViewers: 0,
        uniqueUnloggedViewers: 0,
        viewsByDate: {},
        recentViews: []
      };
    }

    // Get stats from Firestore
    const statsRef = doc(db, 'profileViewStats', username);
    const statsDoc = await getDoc(statsRef);
    
    let totalViews = 0;
    let uniqueLoggedInViewers = 0;
    let uniqueUnloggedViewers = 0;
    
    if (statsDoc.exists()) {
      const stats = statsDoc.data();
      totalViews = stats.totalViews || 0;
      uniqueLoggedInViewers = stats.uniqueLoggedInViewers || 0;
      uniqueUnloggedViewers = stats.uniqueUnloggedViewers || 0;
    }

    // Get all views to recalculate unique viewers accurately
    const allViewsQuery = query(
      collection(db, 'profileViews'),
      where('viewedUsername', '==', username)
    );
    const allViewsSnapshot = await getDocs(allViewsQuery);
    
    // Recalculate unique viewers and count unlogged views
    const uniqueLoggedInViewersSet = new Set();
    let unloggedViewsCount = 0; // Count ALL unlogged views, not unique viewers
    const viewsByDate = {};
    
    // Recalculate from actual views
    
    allViewsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const date = data.date;
      if (date) {
        viewsByDate[date] = (viewsByDate[date] || 0) + 1;
      }
      
      // Match the EXACT logic from display code
      const walletAddr = data.viewerWalletAddress;
      // Same check as in display: hasValidWallet = walletAddress exists, is string, and has length after trim
      const hasValidWallet = walletAddr && typeof walletAddr === 'string' && walletAddr.trim().length > 0;
      const hasSessionId = data.viewerSessionId && typeof data.viewerSessionId === 'string' && data.viewerSessionId.trim().length > 0;
      const hasUsername = data.viewerUsername && typeof data.viewerUsername === 'string' && data.viewerUsername.trim().length > 0;
      const isExplicitlyUnlogged = data.isLoggedIn === false;
      
      // Determine if this view is unlogged
      let isUnloggedView = false;
      
      // CRITICAL: If there's a sessionId, it's ALWAYS unlogged (highest priority)
      if (hasSessionId) {
        isUnloggedView = true;
      }
      // If explicitly marked as unlogged (isLoggedIn === false), count as unlogged
      else if (isExplicitlyUnlogged) {
        isUnloggedView = true;
      }
      // If has valid wallet AND has username, it's definitely logged-in
      else if (hasValidWallet && hasUsername) {
        uniqueLoggedInViewersSet.add(walletAddr.toLowerCase().trim());
        isUnloggedView = false;
      }
      // If has valid wallet BUT NO username = "User" display = COUNT AS UNLOGGED VIEW
      // User confirmed these are unlogged views
      else if (hasValidWallet && !hasUsername) {
        isUnloggedView = true;
      }
      // No valid wallet = unlogged view
      else {
        isUnloggedView = true;
      }
      
      // Count this view if it's unlogged
      if (isUnloggedView) {
        unloggedViewsCount++;
      }
    });
    
    // Log summary (only in development or if there's an issue)
    if (process.env.NODE_ENV === 'development') {
      console.log('Recalculated stats:', {
        uniqueLoggedInViewers: uniqueLoggedInViewersSet.size,
        unloggedViews: unloggedViewsCount,
        totalViews: allViewsSnapshot.docs.length
      });
    }
    
    // Use recalculated counts (more accurate)
    const recalculatedUniqueLoggedInViewers = uniqueLoggedInViewersSet.size;
    const recalculatedUnloggedViews = unloggedViewsCount;
    
    // Get recent views - sort by timestamp descending (latest first)
    const recentViews = allViewsSnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          viewerWalletAddress: data.viewerWalletAddress,
          viewerSessionId: data.viewerSessionId,
          viewerUsername: data.viewerUsername || null, // Include username from view record (stored when view was tracked)
          isLoggedIn: data.isLoggedIn,
          date: data.date,
          timestamp: data.timestamp || 0
        };
      })
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)) // Sort by timestamp descending (latest first)
      .slice(0, 10); // Take top 10

    // Calculate total unique viewers (approximate - logged-in are unique, unlogged views are counted separately)
    const totalUniqueViewers = recalculatedUniqueLoggedInViewers + (unloggedViewsCount > 0 ? 1 : 0);

    return {
      totalViews,
      uniqueViewers: totalUniqueViewers,
      uniqueLoggedInViewers: recalculatedUniqueLoggedInViewers,
      uniqueUnloggedViewers: recalculatedUnloggedViews, // Now counts all unlogged views, not unique viewers
      viewsByDate,
      recentViews // Already sorted with latest first, no need to reverse
    };
  } catch (error) {
    console.error('Error getting profile view stats:', error);
    // Fallback to localStorage if Firestore fails
    try {
      const key = `peopletag_views_${username}`;
      const views = JSON.parse(localStorage.getItem(key) || '[]');
      
      const uniqueLoggedInViewers = new Set();
      let unloggedViewsCount = 0; // Count ALL unlogged views
      
      views.forEach(view => {
        if (view.viewerWalletAddress) {
          uniqueLoggedInViewers.add(view.viewerWalletAddress);
        } else if (view.viewerSessionId || !view.isLoggedIn || view.isLoggedIn === false) {
          // Count as unlogged view
          unloggedViewsCount++;
        }
      });
      
      const totalUniqueViewers = uniqueLoggedInViewers.size + (unloggedViewsCount > 0 ? 1 : 0);
      const viewsByDate = {};
      views.forEach(view => {
        const date = view.date;
        viewsByDate[date] = (viewsByDate[date] || 0) + 1;
      });

      return {
        totalViews: views.length,
        uniqueViewers: totalUniqueViewers,
        uniqueLoggedInViewers: uniqueLoggedInViewers.size,
        uniqueUnloggedViewers: unloggedViewsCount, // Count all unlogged views
        viewsByDate,
        recentViews: views.slice(-10).reverse()
      };
    } catch (localError) {
      console.error('Error getting stats from localStorage fallback:', localError);
      return {
        totalViews: 0,
        uniqueViewers: 0,
        uniqueLoggedInViewers: 0,
        uniqueUnloggedViewers: 0,
        viewsByDate: {},
        recentViews: []
      };
    }
  }
};

/**
 * Track profile interaction (e.g., tag click, social link click)
 * @param {string} interactionType - Type of interaction
 * @param {string} targetUsername - Target profile username
 * @param {Object} metadata - Additional metadata
 */
export const trackInteraction = (interactionType, targetUsername, metadata = {}) => {
  try {
    const interactionData = {
      type: interactionType,
      targetUsername,
      timestamp: Date.now(),
      ...metadata
    };

    const key = 'peopletag_interactions';
    const interactions = JSON.parse(localStorage.getItem(key) || '[]');
    interactions.push(interactionData);
    
    // Keep only last 500 interactions
    if (interactions.length > 500) {
      interactions.shift();
    }
    
    localStorage.setItem(key, JSON.stringify(interactions));
  } catch (error) {
    console.error('Error tracking interaction:', error);
  }
};

/**
 * Get interaction statistics
 * @param {string} username - Username (optional, if not provided returns all interactions)
 * @returns {Object} Interaction statistics
 */
export const getInteractionStats = (username = null) => {
  try {
    const key = 'peopletag_interactions';
    let interactions = JSON.parse(localStorage.getItem(key) || '[]');
    
    if (username) {
      interactions = interactions.filter(i => i.targetUsername === username);
    }

    const byType = {};
    interactions.forEach(interaction => {
      byType[interaction.type] = (byType[interaction.type] || 0) + 1;
    });

    return {
      totalInteractions: interactions.length,
      byType,
      recentInteractions: interactions.slice(-20).reverse()
    };
  } catch (error) {
    console.error('Error getting interaction stats:', error);
    return {
      totalInteractions: 0,
      byType: {},
      recentInteractions: []
    };
  }
};

/**
 * Calculate profile engagement score
 * @param {string} username - Username
 * @returns {number} Engagement score (0-100)
 */
export const calculateEngagementScore = (username) => {
  try {
    const viewStats = getProfileViewStats(username);
    const interactionStats = getInteractionStats(username);
    
    // Simple scoring algorithm
    const viewScore = Math.min(viewStats.totalViews * 2, 50);
    const interactionScore = Math.min(interactionStats.totalInteractions * 5, 50);
    
    return Math.min(100, viewScore + interactionScore);
  } catch (error) {
    console.error('Error calculating engagement score:', error);
    return 0;
  }
};

/**
 * Get most viewed profiles
 * @param {number} limit - Maximum number of profiles to return
 * @returns {Array} Array of {username, views, uniqueViewers}
 */
export const getMostViewedProfiles = (limit = 10) => {
  try {
    const profiles = [];
    
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('peopletag_views_')) {
        const username = key.replace('peopletag_views_', '');
        const stats = getProfileViewStats(username);
        profiles.push({
          username,
          views: stats.totalViews,
          uniqueViewers: stats.uniqueViewers
        });
      }
    }

    return profiles
      .sort((a, b) => b.views - a.views)
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting most viewed profiles:', error);
    return [];
  }
};

/**
 * Track search query
 * @param {string} query - Search query
 * @param {number} resultCount - Number of results
 */
export const trackSearch = (query, resultCount = 0) => {
  try {
    const searchData = {
      query,
      resultCount,
      timestamp: Date.now()
    };

    const key = 'peopletag_searches';
    const searches = JSON.parse(localStorage.getItem(key) || '[]');
    searches.push(searchData);
    
    // Keep only last 200 searches
    if (searches.length > 200) {
      searches.shift();
    }
    
    localStorage.setItem(key, JSON.stringify(searches));
  } catch (error) {
    console.error('Error tracking search:', error);
  }
};

/**
 * Get popular search queries
 * @param {number} limit - Maximum number of queries to return
 * @returns {Array} Array of {query, count}
 */
export const getPopularSearches = (limit = 10) => {
  try {
    const key = 'peopletag_searches';
    const searches = JSON.parse(localStorage.getItem(key) || '[]');
    
    const queryCounts = {};
    searches.forEach(search => {
      const query = search.query.toLowerCase().trim();
      if (query) {
        queryCounts[query] = (queryCounts[query] || 0) + 1;
      }
    });

    return Object.entries(queryCounts)
      .map(([query, count]) => ({ query, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting popular searches:', error);
    return [];
  }
};

/**
 * Clear all analytics data (for privacy/reset)
 */
export const clearAnalyticsData = () => {
  try {
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('peopletag_views_') ||
        key.startsWith('peopletag_interactions') ||
        key.startsWith('peopletag_searches')
      )) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (error) {
    console.error('Error clearing analytics data:', error);
    return false;
  }
};

/**
 * Export analytics data as JSON
 * @returns {Object} Analytics data
 */
export const exportAnalyticsData = () => {
  try {
    const analytics = {
      views: {},
      interactions: JSON.parse(localStorage.getItem('peopletag_interactions') || '[]'),
      searches: JSON.parse(localStorage.getItem('peopletag_searches') || '[]'),
      exportedAt: new Date().toISOString()
    };

    // Collect all view data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('peopletag_views_')) {
        const username = key.replace('peopletag_views_', '');
        analytics.views[username] = JSON.parse(localStorage.getItem(key) || '[]');
      }
    }

    return analytics;
  } catch (error) {
    console.error('Error exporting analytics data:', error);
    return null;
  }
};

